/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

// This file contains the first Finsemble code to execute on startup. It's correspond HTML file, systemManager.html is pointed to by manifest URL.

// ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Overview in Real Time Board
// https://realtimeboard.com/welcomeonboard/dqbvK59KjETgD8Yc2VmW4BSpCieLXc4jfwmFMkgFTVRqLba0sCDFO2H3PHlGCpqi
// ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { Logger } from "../../clients/logger";
import SystemLog from "./systemLog";
import { BootEngine } from "./bootEngine";
import { ServiceLauncher } from "./serviceLauncher";
import { ShutdownManager } from "./shutDownManager";
import { System } from "../../common/system";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { SchemaUtilInstance as SchemaUtil } from "../config/schemaUtil";
import { DeprecatedConfigUtilInstance as DeprecatedConfigUtil } from "../config/migratingDeprecatedConfigUtil";
import { InitializeDeepLinkingTask } from "./bootTasks/initializeDeepLinkingTask";
import { InitializeSystemStateHandersTask } from "./bootTasks/initializeSystemStateHandersTask";
import { InitializeRouterTask } from "./bootTasks/initializeRouterTask";
import { InitializeFinsemblePubsubTask } from "./bootTasks/initializeFinsemblePubsubTask";
import { WaitForAuthenticatedTask } from "./bootTasks/waitForAuthenticatedTask";
import { InitializeSystemManagerAPITask } from "./bootTasks/initializeSystemManagerAPITask";
import { StartLoggerTask } from "./bootTasks/startLoggerTask";
import { RegisterHotkeysTask } from "./bootTasks/registerHotKeysTask";
import { SetupSearchLauncherTask } from "./bootTasks/setupSearchLauncherTask";
import { LoadUserDefinedComponentsTask } from "./bootTasks/loadUserDefinedComponentsTask";
import { LoadAppDInstalledComponentsTask } from "./bootTasks/loadAppDInstalledComponentsTask";
import { LoadSystemTrayIconTask } from "./bootTasks/loadSystemTrayIconTask";
import { ForceErrorTestTask } from "./bootTasks/testTasks/forceErrorTestTask";
import { CheckpointErrorTestTask } from "./bootTasks/testTasks/checkpointErrorTestTask";
import { CheckpointBadDependencyTestTask } from "./bootTasks/testTasks/checkpointBadDependencyTestTask";
import { CheckpointCircularDependencyTestTask } from "./bootTasks/testTasks/checkpointCircularDependencyTestTask";
import { BootTaskCallbackInterface } from "./types";
import { UpdateServiceLauncherConfigTask } from "./bootTasks/updateServiceLauncherConfigTask";
import { CheckForScheduledRestartTask } from "./bootTasks/checkForScheduledRestartTask";
import { CheckForScheduledShutdownTask } from "./bootTasks/checkForScheduledShutdownTask";
import { HideSplashScreenTask } from "./bootTasks/hideSplashScreenTask";
import { WaitForUpdateDownloadTask } from "./bootTasks/waitForUpdateDownloadTask";
import { AddAppDefinitionsToDesktopAgentTask } from "./bootTasks/addAppDefinitionsToDesktopAgentTask";
import { RouterClient } from "../../clients/routerClient";
import { FEAGlobals } from "../../common/Globals";

type Manifest = Record<string, any>;

let debuggerInfo: {
	systemManager?: SystemManager;
	bootEngine?: BootEngine;
	helpData?: Function;
	mainWindow?: any;
	openCentralLogger?: Function;
	showDeveloperTools?: Function;
} = window as any;

// catch and errors here that were missed and log them
window.addEventListener("error", (errorObject) => {
	// // take the event error object and strip off irrelevant parts of the call stack, format the message, then output to the system log since error here is likely catastrophic
	const stack =
		errorObject.error && errorObject.error.stack
			? errorObject.error.stack.substring(errorObject.error.stack.search("at "))
			: "";
	const systemLogErr = `System Manager: ${errorObject.message}<br />File: ${errorObject.filename}<br />Line: ${errorObject.lineno}<br />Column: ${errorObject.colno}<br />${stack}`;
	SystemLog.error({ leadingBlankLine: true }, systemLogErr);
	return false;
});

/**
 * The system manger boots Finsemble and hosts system-wide resources including the system log.
 * The system manager's URL is identified in the manifest -- it always starts first, then the
 * bootEngine starts the other services and components.
 */
export class SystemManager {
	onErrorMakeSystemManagerVisible: boolean;

	bootEngine: BootEngine | undefined;

	manifest: Manifest;

	serviceLauncher: ServiceLauncher | undefined;

	shutdownManager: ShutdownManager | undefined;

	constructor(params: { manifest: Manifest; onErrorMakeSystemManagerVisible: boolean }) {
		console.debug("Starting System Manager", params);

		// First send handshake so FEA knows Finsemble started (after this handshake Finsemble can handle any startup issues). We do this so that FEA has positive confirmation that Finsemble has started.
		// Without this (assuming requireHandshake is enabled in FEA manifest), FEA will timeout and present a user notification (but otherwise continue normally)
		System.startupApplicationHandshake();

		this.manifest = params.manifest;
		this.onErrorMakeSystemManagerVisible = params.onErrorMakeSystemManagerVisible;
	}

	/**
	 * Very config against boot schema and report errors as needed. If any config values are invalid then Finsemble might not start correctly.
	 * @param manifest the initial boot manifest
	 */
	static async verifyConfig(manifest): Promise<boolean> {
		let isSuccessful = true;
		const SUBSCHEMA_IDENTIFIER = "#/definitions/BootManifestSubschema";
		const err = await SchemaUtil.validateConfig(manifest, SUBSCHEMA_IDENTIFIER);
		if (err) {
			// any config problems at this level might be catastrophic and without a central logger, so report error to system log
			SystemLog.error(
				{},
				'Boot Config Errors: Ctrl+Shift-I on this window to see "JSON Schema Validation Errors" in System Manager\'s devTool console.'
			);
			console.error(err);
			isSuccessful = false;
		}
		return isSuccessful;
	}

	/**
	 * help function to display on console the key diagnostic data
	 */
	public helpData() {
		console.log("1) finsemble.bootConfig onErrorMakeSystemManagerVisible:", this.onErrorMakeSystemManagerVisible);
		console.log("");
		this.bootEngine?.helpData();
		return "helpData complete"; // for cleaner console output
	}

	/**
	 * Creates Boot Engine and runs it to bring on Finsemble.
	 */
	private boot() {
		try {
			this.shutdownManager = new ShutdownManager({
				manifest: this.manifest,
			});
			this.serviceLauncher = new ServiceLauncher({
				manifest: this.manifest,
				shutdownManager: this.shutdownManager,
			});

			debuggerInfo.bootEngine = this.bootEngine = new BootEngine(
				this.manifest,
				this.serviceLauncher,
				this.shutdownManager
			);

			this.registerBootTasks(this.bootEngine, this.manifest, this.serviceLauncher);
			this.bootEngine.run();
		} catch (err: any) {
			SystemLog.error({ leadingBlankLine: true }, err);
		}
	}

	public async start() {
		// Make sure that essential config is available
		const validConfig = await SystemManager.verifyConfig(this.manifest);
		if (validConfig) {
			Logger.system.log("APPLICATION LIFECYCLE:STARTUP: Finsemble Version", this.manifest.finsemble.system.FSBLVersion);

			// left public for testing and debugging
			if (this.manifest.finsemble.debugSystemManager) {
				System.showDeveloperTools(window.name, null, () => {
					// Without this timeout the debugger is never hit even though it is in the callback of showDeveloperTools.
					setTimeout(() => {
						debugger;
						this.boot();
					}, 2000);
				});
			} else {
				this.boot();
			}
		} else {
			SystemLog.error({ leadingBlankLine: true }, "FATAL ERROR: Prematurely terminating Finsemble.");
		}
	}

	/**
	 * Registers all the boot tasks before running bootEngine
	 * @param bootEngine
	 * @param manifest
	 * @param serviceLauncher
	 */
	private registerBootTasks(bootEngine: BootEngine, manifest, serviceLauncher: ServiceLauncher) {
		// var to assign arrow functions to so won't be anonymous in stack trace
		this.bootEngine?.registerBootTask("initializeDeepLinkingTask", (doneCallback: BootTaskCallbackInterface) => {
			const initializeDeepLinkingTask = new InitializeDeepLinkingTask();
			initializeDeepLinkingTask.start(doneCallback);
		});
		this.bootEngine?.registerBootTask("initializeRouterTask", (doneCallback: BootTaskCallbackInterface) => {
			const initializeRouterTask = new InitializeRouterTask({
				manifest,
				serviceLauncher,
			});
			initializeRouterTask.start(doneCallback);
		});
		this.bootEngine?.registerBootTask("updateServiceLauncherConfigTask", (doneCallback: BootTaskCallbackInterface) => {
			const updateServiceLauncherConfigTask = new UpdateServiceLauncherConfigTask({ serviceLauncher });
			updateServiceLauncherConfigTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("initializeFinsemblePubsubTask", (doneCallback: BootTaskCallbackInterface) => {
			const initializeFinsemblePubsubTask = new InitializeFinsemblePubsubTask();
			initializeFinsemblePubsubTask.start(doneCallback);
		});
		this.bootEngine?.registerBootTask("initializeSystemStateHandersTask", (doneCallback: BootTaskCallbackInterface) => {
			const initializeSystemStateHandlersTask = new InitializeSystemStateHandersTask();
			initializeSystemStateHandlersTask.start(doneCallback);
		});
		this.bootEngine?.registerBootTask("waitForUpdateDownloadTask", (doneCallback: BootTaskCallbackInterface) => {
			const waitForUpdateDownloadTask = new WaitForUpdateDownloadTask();
			waitForUpdateDownloadTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("waitForAuthenticatedTask", (doneCallback: BootTaskCallbackInterface) => {
			const waitForAuthenticatedTask = new WaitForAuthenticatedTask();
			waitForAuthenticatedTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("initializeSystemManagerAPITask", (doneCallback: BootTaskCallbackInterface) => {
			const initializeSystemManagerAPITask = new InitializeSystemManagerAPITask({ bootEngine, systemLog: SystemLog });
			initializeSystemManagerAPITask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("startLoggerTask", (doneCallback: BootTaskCallbackInterface) => {
			const startLoggerTask = new StartLoggerTask();
			startLoggerTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("loadUserDefinedComponentsTask", (doneCallback: BootTaskCallbackInterface) => {
			const loadUserDefinedComponentsTask = new LoadUserDefinedComponentsTask();
			loadUserDefinedComponentsTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("loadAppDInstalledComponentsTask", (doneCallback: BootTaskCallbackInterface) => {
			const loadAppDInstalledComponentsTask = new LoadAppDInstalledComponentsTask();
			loadAppDInstalledComponentsTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("registerHotkeysTask", (doneCallback: BootTaskCallbackInterface) => {
			const registerHotkeysTask = new RegisterHotkeysTask();
			registerHotkeysTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("setupSearchLauncherTask", (doneCallback: BootTaskCallbackInterface) => {
			const setupSearchLauncherTask = new SetupSearchLauncherTask();
			setupSearchLauncherTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("loadSystemTrayIconTask", (doneCallback: BootTaskCallbackInterface) => {
			const loadSystemTrayIconTask = new LoadSystemTrayIconTask();
			loadSystemTrayIconTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("checkForScheduledRestartTask", (doneCallback: BootTaskCallbackInterface) => {
			const checkForScheduledRestartTask = new CheckForScheduledRestartTask();
			checkForScheduledRestartTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("checkForScheduledShutdownTask", (doneCallback: BootTaskCallbackInterface) => {
			const checkForScheduledShutdownTask = new CheckForScheduledShutdownTask();
			checkForScheduledShutdownTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask("hideSplashScreenTask", (doneCallback: BootTaskCallbackInterface) => {
			const hideSplashScreenTask = new HideSplashScreenTask();
			hideSplashScreenTask.start(doneCallback);
		});

		this.bootEngine?.registerBootTask(
			"addAppDefinitionsToDesktopAgentTask",
			(doneCallback: BootTaskCallbackInterface) => {
				const addAppDefinitionsToDesktopAgentTask = new AddAppDefinitionsToDesktopAgentTask();
				addAppDefinitionsToDesktopAgentTask.start(doneCallback);
			}
		);

		// test tasks -- load them so can test with only a config change. By default they are disabled (i.e. their bootParams.autostart is false)
		// the start function for these test tasks can be passed in directly since there's no binding problem (the test tasks won't reference `this`)
		this.bootEngine?.registerBootTask("forceErrorTestTask", new ForceErrorTestTask().start);

		this.bootEngine?.registerBootTask("checkpointErrorTestTask", new CheckpointErrorTestTask().start);

		this.bootEngine?.registerBootTask(
			"checkpointCircularDependencyTestTask",
			new CheckpointBadDependencyTestTask().start
		);

		this.bootEngine?.registerBootTask(
			"checkpointBadDependencyTestTask",
			new CheckpointCircularDependencyTestTask().start
		);

		this.bootEngine?.registerBootTask(
			"checkpointBadDependencyTestTask",
			new CheckpointCircularDependencyTestTask().start
		);
	}
}

const main = async () => {
	// first code to run after the underlying container (e.g. Electron) is ready. Display any errors in console in case logger doesn't come up
	FEAGlobals.finsembleServices = [
		{
			name: "System Manager",
		},
	];

	const { err, data: manifest } = await ConfigUtil.getInitialManifest();
	if (!err && manifest) {
		// initialize system log first since it may be needed for error output
		const onErrorMakeSystemManagerVisible = ConfigUtil.getDefault(
			manifest,
			"manifest.finsemble.bootConfig.onErrorMakeSystemManagerVisible",
			false
		);
		SystemLog.initialize(!!onErrorMakeSystemManagerVisible);

		await DeprecatedConfigUtil.modifyConfigBasedOnDeprecatedDefinitions("bootConfig", manifest);
		await SchemaUtil.walkSchemaAndSetConfigDefaults(manifest);

		// make sure the essential config is available
		await SystemManager.verifyConfig(manifest);

		// for diagnostics and testing
		debuggerInfo.mainWindow = System.Window.getCurrent();

		Logger.system.log("APPLICATION LIFECYCLE:STARTUP: Finsemble Version", manifest.finsemble.system.FSBLVersion);

		// get the "bootstrap" manifest, which is the minimum config necessary to start Finsemble.
		// the following logger and console output is referenced in FAQ for diagnostics, so don't change
		Logger.console.log("forceObjectsToLogger", "APPLICATION LIFECYCLE:STARTUP: *** Initial manifest ***", manifest);

		const systemManager = new SystemManager({ manifest, onErrorMakeSystemManagerVisible });
		// starts Finsemble
		systemManager.start();
		// window vars for debugging + allows helpData() to be run directly from chromium console (at window level)
		debuggerInfo.systemManager = systemManager;
		debuggerInfo.helpData = systemManager.helpData.bind(systemManager);
		debuggerInfo.openCentralLogger = () => {
			RouterClient.transmit("CentralConsole-Show", true);
		};
		debuggerInfo.showDeveloperTools = () => {
			System.showDeveloperTools(window.name, null);
		};
	} else {
		Logger.console.error(`systemManager.main() error getting initial manifest ${err}`);
	}
};

main();
